import { useEffect, useState, useCallback, useMemo, type FormEvent } from 'react'
import { useAuth } from '../context/AuthContext'
import {
  fetchWikiTree,
  adminListGrants,
  adminCreateGrant,
  adminDeleteGrant,
  adminListUsers,
  type AdminGrant,
  type AdminUser,
} from '../api/client'
import type { WikiTree, WikiTreeShelf, WikiTreeBook } from '../types/wiki'
import { Shield, Plus, Trash2, ChevronRight, ChevronDown, ShieldAlert, AlertCircle, Loader2, Users, Info } from 'lucide-react'

const PERMISSIONS = ['view', 'edit', 'admin'] as const

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-gray-950 border border-gray-800 rounded-xl shadow-xl text-gray-200"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

export default function AdminPermissionsPage() {
  const { role } = useAuth()

  const [tree, setTree] = useState<WikiTree | null>(null)
  const [grants, setGrants] = useState<AdminGrant[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Selected node for grant panel
  const [selectedShelf, setSelectedShelf] = useState<WikiTreeShelf | null>(null)
  const [selectedBook, setSelectedBook] = useState<WikiTreeBook | null>(null)
  const [expandedShelves, setExpandedShelves] = useState<Set<string>>(new Set())

  // Add grant form
  const [addGrantUser, setAddGrantUser] = useState('')
  const [addGrantPerm, setAddGrantPerm] = useState('view')
  const [adding, setAdding] = useState(false)
  const [addErr, setAddErr] = useState<string | null>(null)

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<AdminGrant | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  const isAdmin = role === 'admin'

  const loadAll = useCallback(async () => {
    const [wikiTreeData, grantsData, usersData] = await Promise.all([
      fetchWikiTree(),
      adminListGrants(),
      adminListUsers(),
    ])
    setTree(wikiTreeData)
    setGrants(grantsData)
    setUsers(usersData)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    void (async () => {
      try {
        await loadAll()
      } catch {
        if (!cancelled) setErr('Failed to load permissions data.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [loadAll])

  // Filter grants for current selection
  const currentGrants = useMemo(() => {
    return grants.filter((g) => {
      if (selectedBook) return g.book_path === selectedBook.slug
      if (selectedShelf) {
        if (g.shelf_slug === selectedShelf.slug && !g.book_path) return true
        return false
      }
      return false
    })
  }, [grants, selectedShelf, selectedBook])

  // Book-level grants that live under the currently selected shelf
  const bookGrantsUnderShelf = useMemo(() => {
    if (!selectedShelf) return []
    const bookSlugs = new Set(selectedShelf.books.map((b) => b.slug))
    return grants.filter((g) => g.book_path && bookSlugs.has(g.book_path))
  }, [grants, selectedShelf])

  // Shelf grants on the parent shelf of the currently selected book
  const parentShelfGrants = useMemo(() => {
    if (!selectedBook || !selectedShelf) return []
    return grants.filter((g) => g.shelf_slug === selectedShelf.slug && !g.book_path)
  }, [grants, selectedBook, selectedShelf])

  const activeUsernames = useMemo(() => new Set(users.map((u) => u.username)), [users])

  // Admin users bypass RBAC entirely (see server/wiki/rbac.ts), so explicit
  // grants to them would be dead weight — exclude them and say so.
  const userOptions = useMemo(() => users.filter((u) => u.role !== 'admin'), [users])

  const toggleShelf = (slug: string) => {
    setExpandedShelves((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  const selectShelf = (shelf: WikiTreeShelf) => {
    setSelectedShelf(shelf)
    setSelectedBook(null)
    setAddGrantUser('')
    setAddGrantPerm('view')
    setAddErr(null)
    setDeleteErr(null)
  }

  const selectBook = (shelf: WikiTreeShelf, book: WikiTreeBook) => {
    setSelectedShelf(shelf)
    setSelectedBook(book)
    setAddGrantUser('')
    setAddGrantPerm('view')
    setAddErr(null)
    setDeleteErr(null)
  }

  // ----- Add grant -----
  const handleAddGrant = async () => {
    if (!addGrantUser || !selectedShelf) return
    setAdding(true)
    setAddErr(null)
    try {
      const input: {
        shelf_slug?: string
        book_path?: string
        username: string
        permission: string
      } = {
        username: addGrantUser,
        permission: addGrantPerm,
      }
      if (selectedBook) {
        input.book_path = selectedBook.slug
      } else {
        input.shelf_slug = selectedShelf.slug
      }
      const result = await adminCreateGrant(input)
      // The API returns id/username/permission (+ node fields); make the new
      // grant match the list shape so it shows up immediately.
      setGrants((prev) => [
        ...prev,
        { ...result, shelf_slug: input.shelf_slug ?? null, book_path: input.book_path ?? null },
      ])
      setAddGrantUser('')
      setAddGrantPerm('view')
      setAddErr(null)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to add grant.'
          : 'Failed to add grant.'
      setAddErr(msg)
    } finally {
      setAdding(false)
    }
  }

  const onAddGrantSubmit = (e: FormEvent) => {
    e.preventDefault()
    void handleAddGrant()
  }

  // ----- Delete grant -----
  const handleDeleteGrant = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setDeleteErr(null)
    try {
      await adminDeleteGrant(deleteTarget.id)
      setGrants((prev) => prev.filter((g) => g.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to delete grant.'
          : 'Failed to delete grant.'
      setDeleteErr(msg)
    } finally {
      setDeleting(false)
    }
  }

  // ----- Access denied -----
  if (!isAdmin) {
    return (
      <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
        <div className="flex items-center gap-3 text-amber-400 bg-amber-950/30 border border-amber-900/40 rounded-lg p-6">
          <ShieldAlert className="w-8 h-8 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold">Access denied</h2>
            <p className="text-amber-300/70 text-sm mt-1">You need the admin role to manage permissions.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-6 h-6 text-primary-400" />
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Permissions</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {grants.length} grant{grants.length === 1 ? '' : 's'} across {tree?.shelves.length ?? 0} shelves
          </p>
        </div>
      </div>

      {/* Error banner */}
      {err && (
        <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 mb-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading permissions…
        </div>
      )}

      {!loading && tree && (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* ── Wiki tree panel ── */}
          <div className="lg:w-1/2 rounded-lg border border-gray-800 bg-gray-900/40 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800 text-sm font-medium text-gray-300">
              Wiki structure
            </div>
            <div className="divide-y divide-gray-800/60">
              {tree.shelves.length === 0 && (
                <p className="p-6 text-center text-gray-500 text-sm">No shelves exist yet.</p>
              )}
              {tree.shelves.map((shelf) => {
                const isSelected = selectedShelf?.slug === shelf.slug && !selectedBook
                const isExpanded = expandedShelves.has(shelf.slug)
                const shelfGrants = grants.filter((g) => g.shelf_slug === shelf.slug && !g.book_path)
                return (
                  <div key={shelf.id}>
                    {/* Shelf row */}
                    <button
                      type="button"
                      onClick={() => {
                        toggleShelf(shelf.slug)
                        selectShelf(shelf)
                      }}
                      className={`flex items-center gap-2 w-full text-left px-4 py-3 text-sm min-h-[44px] hover:bg-gray-800/40 transition-colors ${
                        isSelected ? 'bg-primary-900/20 border-l-2 border-primary-500' : ''
                      }`}
                    >
                      {shelf.books.length > 0 ? (
                        isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
                        )
                      ) : (
                        <span className="w-4 shrink-0" />
                      )}
                      <Shield className="w-4 h-4 text-primary-400 shrink-0" />
                      <span className="text-gray-100 font-medium truncate">{shelf.name}</span>
                      {shelfGrants.length > 0 && (
                        <span className="ml-auto text-xs text-primary-400 bg-primary-900/30 px-2 py-0.5 rounded-full">
                          {shelfGrants.length}
                        </span>
                      )}
                    </button>

                    {/* Books (expanded) */}
                    {isExpanded &&
                      shelf.books.map((book) => {
                        const isBookSelected = selectedBook?.slug === book.slug
                        const bookGrants = grants.filter((g) => g.book_path === book.slug)
                        return (
                          <button
                            key={book.id}
                            type="button"
                            onClick={() => selectBook(shelf, book)}
                            className={`flex items-center gap-2 w-full text-left pl-10 pr-4 py-2.5 text-sm min-h-[44px] hover:bg-gray-800/40 transition-colors ${
                              isBookSelected ? 'bg-primary-900/20 border-l-2 border-primary-500' : ''
                            }`}
                          >
                            <span className="text-gray-400 text-xs">📄</span>
                            <span className="text-gray-300 truncate">{book.name}</span>
                            {bookGrants.length > 0 && (
                              <span className="ml-auto text-xs text-primary-400 bg-primary-900/30 px-2 py-0.5 rounded-full">
                                {bookGrants.length}
                              </span>
                            )}
                          </button>
                        )
                      })}
                  </div>
                )
              })}
            </div>
          </div>

          {/* ── Grant detail panel ── */}
          <div className="lg:w-1/2 space-y-4">
            {!selectedShelf && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-500 border border-dashed border-gray-800 rounded-lg">
                <Shield className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">Select a shelf or book to manage grants</p>
              </div>
            )}

            {selectedShelf && (
              <>
                {/* Selected node label */}
                <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-4">
                  <p className="text-xs text-gray-500 mb-1">
                    {selectedBook ? 'Book' : 'Shelf'}
                  </p>
                  <p className="text-gray-100 font-semibold">
                    {selectedBook ? selectedBook.name : selectedShelf.name}
                  </p>
                </div>

                {/* Add grant form */}
                <form
                  onSubmit={onAddGrantSubmit}
                  className="rounded-lg border border-gray-800 bg-gray-900/40 p-4 space-y-3"
                >
                  <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-primary-400" />
                    Add grant
                  </h3>

                  {addErr && (
                    <div className="text-sm text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">
                      {addErr}
                    </div>
                  )}

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="text-gray-400">User</span>
                    <select
                      value={addGrantUser}
                      onChange={(e) => setAddGrantUser(e.target.value)}
                      className="min-h-[44px] bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select user…</option>
                      {userOptions.map((u) => (
                        <option key={u.id} value={u.username}>
                          {u.username} ({u.role})
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-xs text-gray-500 -mt-1">
                    Admin users are not listed — they already have full access to the archive.
                    {userOptions.length === 0 && ' Create a non-admin user first.'}
                  </p>

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="text-gray-400">Permission</span>
                    <select
                      value={addGrantPerm}
                      onChange={(e) => setAddGrantPerm(e.target.value)}
                      className="min-h-[44px] bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {PERMISSIONS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </label>

                  {!selectedBook && (
                    <p className="flex items-start gap-2 text-xs text-gray-500">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>Shelf grants apply to every book in this shelf.</span>
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={adding || !addGrantUser}
                    className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50 w-full justify-center"
                  >
                    {adding ? 'Adding…' : 'Add grant'}
                  </button>
                </form>

                {/* Current grants list */}
                <div className="rounded-lg border border-gray-800 bg-gray-900/40 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-800 text-sm font-medium text-gray-300 flex items-center justify-between">
                    <span>Grants ({currentGrants.length})</span>
                  </div>
                  {currentGrants.length === 0 && (
                    <p className="p-6 text-center text-gray-500 text-sm">No grants for this node.</p>
                  )}
                  <div className="divide-y divide-gray-800/60">
                    {currentGrants.map((g) => (
                      <div key={g.id} className="flex items-center justify-between px-4 py-3 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <Users className="w-4 h-4 text-gray-500 shrink-0" />
                          <span className="text-sm text-gray-200 truncate">{g.username}</span>
                          {!activeUsernames.has(g.username) && (
                            <span className="text-xs text-gray-500 shrink-0" title="This user no longer exists">
                              (deleted)
                            </span>
                          )}
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                              g.permission === 'admin'
                                ? 'bg-purple-900/50 text-purple-200 border border-purple-700/40'
                                : g.permission === 'edit'
                                ? 'bg-blue-900/50 text-blue-200 border border-blue-700/40'
                                : 'bg-green-900/50 text-green-200 border border-green-700/40'
                            }`}
                          >
                            {g.permission}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteTarget(g)
                            setDeleteErr(null)
                          }}
                          className="p-2 rounded-lg text-gray-400 hover:bg-red-900/40 hover:text-red-400 shrink-0"
                          title="Remove grant"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {selectedBook && parentShelfGrants.length > 0 && (
                    <p className="px-4 py-3 border-t border-gray-800/60 text-xs text-gray-500 flex items-start gap-2">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        This book also inherits {parentShelfGrants.length} shelf-level grant
                        {parentShelfGrants.length === 1 ? '' : 's'} from{' '}
                        <span className="text-gray-300">{selectedShelf.name}</span>.
                      </span>
                    </p>
                  )}

                  {!selectedBook && bookGrantsUnderShelf.length > 0 && (
                    <p className="px-4 py-3 border-t border-gray-800/60 text-xs text-gray-500 flex items-start gap-2">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        {bookGrantsUnderShelf.length} book-level grant
                        {bookGrantsUnderShelf.length === 1 ? '' : 's'} also apply on individual books in this shelf.
                      </span>
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Delete grant confirmation ── */}
      {deleteTarget && (
        <ModalOverlay onClose={() => setDeleteTarget(null)}>
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-red-400" />
              <div>
                <h2 className="text-lg font-semibold text-gray-100">Remove grant</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Revoke <span className="font-semibold text-gray-200">{deleteTarget.permission}</span> access
                  for <span className="font-semibold text-gray-200">{deleteTarget.username}</span>?
                </p>
              </div>
            </div>

            {deleteErr && (
              <div className="text-sm text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">
                {deleteErr}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void handleDeleteGrant()}
                className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-red-900/40 border border-red-700/50 text-red-200 hover:bg-red-900/60 disabled:opacity-50"
              >
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}
